import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { EnterpriseInfoType } from '../../../types/quick/quick-type';
import useValidate, { passwordRegex } from '../../hooks/useValidate';

const INPUT_STYLE =
  'w-full h-full py-4 px-3 rounded-lg border border-borderLightGray focus:border-primary max-md:p-3 max-md:placeholder:text-sm';
const DIV_STYLE = 'flex flex-col space-y-2 max-2xl:text-base text-lg';
const REQUIRED_STYLE = 'not-italic text-primary text-[8px]';

type Props = {
  setEnterpriseInfo: Dispatch<SetStateAction<EnterpriseInfoType>>;
  enterpriseInfo: EnterpriseInfoType;
  duplicateChecked: boolean;
  checkEmailDuplicate: (memberEmail: string) => Promise<void>;
  phrase: string;
  setPhrase: Dispatch<SetStateAction<string>>;
};

export default function EnterpriseInputForm({
  setEnterpriseInfo,
  enterpriseInfo,
  duplicateChecked,
  checkEmailDuplicate,
  phrase,
  setPhrase,
}: Props) {
  const { memberPwd } = enterpriseInfo;

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEnterpriseInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const checkDuplicate = () => {
    checkEmailDuplicate(enterpriseInfo.memberEmail);
  };

  return (
    <>
      <div className={DIV_STYLE}>
        <div className="font-medium flex items-center space-x-1">
          <span>회사명</span>
          <i className={REQUIRED_STYLE}>●</i>
        </div>
        <div className="flex space-x-3">
          <input
            className={INPUT_STYLE}
            autoFocus
            tabIndex={1}
            type="text"
            spellCheck={false}
            maxLength={20}
            name="companyNm"
            autoComplete="off"
            onChange={handleOnChange}
            placeholder="사업자등록증에 기재된 회사이름을 기재해주세요."
          />
        </div>
      </div>
      <div className={DIV_STYLE}>
        <div className="font-medium flex items-center space-x-1">
          <span>담당자명</span>
          <i className={REQUIRED_STYLE}>●</i>
        </div>
        <div className="flex space-x-3">
          <input
            className={INPUT_STYLE}
            tabIndex={2}
            spellCheck={false}
            type="text"
            maxLength={20}
            name="memberFnm1"
            autoComplete="off"
            onChange={handleOnChange}
            placeholder="담당자명을 입력해주세요."
          />
        </div>
      </div>
      <div className={DIV_STYLE}>
        <div className="font-medium flex items-center space-x-1">
          <span>아이디(이메일)</span>
          <i className={REQUIRED_STYLE}>●</i>
        </div>
        <div className="flex space-x-3">
          <input
            className={`${INPUT_STYLE} ${
              duplicateChecked ? 'border-success' : phrase ? 'border-error' : ''
            }`}
            tabIndex={3}
            maxLength={60}
            type="email"
            name="memberEmail"
            spellCheck={false}
            autoComplete="off"
            placeholder="아이디가 될 이메일 정보를 입력해주세요."
            onKeyDown={({ key }) => key === 'Enter' && checkDuplicate()}
            onChange={(e) => {
              setPhrase('');
              handleOnChange(e);
            }}
          />
          <button
            className={`rounded-lg border border-black hover:bg-gray5 min-w-[144px] max-md:min-w-fit max-md:px-4`}
            onClick={checkDuplicate}
          >
            중복확인
          </button>
        </div>
        <div className="flex relative">
          <span className="text-textGray max-2xl:text-xs text-sm">
            영문, 숫자, 일부기호(-, _, ., @,)만 입력 가능
          </span>
          {phrase && (
            <span
              className={`${
                duplicateChecked ? 'text-success' : phrase ? 'text-error' : ''
              } max-2xl:text-xs text-sm absolute -bottom-[22px]`}
            >
              {phrase}
            </span>
          )}
        </div>
      </div>
      <div className={DIV_STYLE}>
        <div className="font-medium flex items-center space-x-1">
          <span>비밀번호</span>
          <i className={REQUIRED_STYLE}>●</i>
        </div>
        <div className="flex space-x-3">
          <input
            className={`${INPUT_STYLE} ${
              passwordRegex.test(memberPwd)
                ? '!border-success'
                : useValidate(memberPwd)
                ? '!border-error'
                : ''
            }`}
            tabIndex={4}
            type="password"
            name="memberPwd"
            maxLength={16}
            onChange={handleOnChange}
            autoComplete="off"
            placeholder="비밀번호를 입력해주세요."
          />
        </div>
        <div className="flex relative">
          <span className="text-textGray max-2xl:text-xs text-sm">
            영문, 숫자, 특수문자(!, @, $, ^, *, -, _)를 혼합하여 8~16자로 사용
          </span>
          {memberPwd && (
            <span className="text-error max-2xl:text-xs text-sm absolute -bottom-[22px]">
              {useValidate(memberPwd)}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
